import { csrfHeadersForRequest } from './csrf';
import { encodeBase64 } from '../crypto/primitives';
import type {
  SedrRequirement,
  SedrFile,
  SedrAuditEvent,
  SedrCategory,
  SedrPhasesList,
  SedrGuestRoomContext,
  SedrComment,
  CreateSedrFilePayload,
} from './sedr';

async function guestRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers({
    ...csrfHeadersForRequest(path, init.method),
    ...(init.headers ?? {}),
  });
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (!response.ok) {
    let code = 'request_failed';
    try {
      const body = (await response.json()) as { code?: string; error?: string };
      if (body.code) code = body.code;
      else if (body.error) code = body.error;
    } catch {
      /* ignore */
    }
    if (code === 'request_failed' && (response.status === 429 || response.status === 503)) {
      code = 'rate_limited';
    }
    const error = new Error(code) as Error & { status?: number; code?: string };
    error.status = response.status;
    error.code = code;
    throw error;
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export interface GuestSession {
  userId: string;
  displayName: string;
  email: string | null;
  organization: string | null;
  vaultSetupComplete: boolean;
  vaultVersion: number;
  totpEnrolled: boolean;
  totpVerified: boolean;
}

export interface GuestInviteGate {
  inviteId: string;
  roomId: string;
  roomServerToken: string;
  displayName: string;
  organization: string;
  email: string | null;
  guestRole: string;
  allowCommenting: boolean;
  nonce: string;
  requireGuestTotp: boolean;
}

export interface GuestInviteVerify {
  inviteId: string;
  roomId: string;
  roomServerToken: string;
  envelopeBlobId: string;
  displayName: string;
  organization: string;
  email: string | null;
  guestRole: string;
  allowCommenting: boolean;
}

export interface GuestRoomSummary {
  roomId: string;
  serverToken: string;
  title: string;
  clientOrganization: string;
  guestRole: string;
  allowCommenting: boolean;
  requireGuestTotp: boolean;
  lifecycleState: string;
  archived: boolean;
  lastModifiedAt: string;
  lastViewedAt: string | null;
}

export interface GuestTotpStart {
  pendingToken: string;
  secret: string | null;
  otpauthUri: string | null;
}

function roomBase(roomId: string): string {
  return `/api/guest/rooms/${encodeURIComponent(roomId)}`;
}

export const guestApi = {
  loginChallenge: (email: string) =>
    guestRequest<{ vaultSalt: string }>(
      `/api/guest/login-challenge?email=${encodeURIComponent(email)}`,
    ),
  login: (email: string, loginVerifier: string) =>
    guestRequest<GuestSession>('/api/guest/login', {
      method: 'POST',
      body: JSON.stringify({ email, loginVerifier }),
    }),
  logout: () => guestRequest<void>('/api/guest/logout', { method: 'POST' }),
  session: () => guestRequest<GuestSession>('/api/guest/session'),
  updateLoginVerifier: (loginVerifier: string) =>
    guestRequest<void>('/api/guest/login-verifier', {
      method: 'PUT',
      body: JSON.stringify({ loginVerifier }),
    }),
  totpStatus: () => guestRequest<{ enrolled: boolean; confirmedAt: string | null }>('/api/guest/totp/status'),
  totpEnrollStart: () =>
    guestRequest<GuestTotpStart>('/api/guest/totp/enroll/start', { method: 'POST' }),
  totpEnrollConfirm: (pendingToken: string, code: string) =>
    guestRequest<GuestSession>('/api/guest/totp/enroll/confirm', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, code }),
    }),
  totpReplaceStart: () =>
    guestRequest<GuestTotpStart>('/api/guest/totp/replace/start', { method: 'POST' }),
  totpReplaceConfirm: (pendingToken: string, code: string) =>
    guestRequest<GuestSession>('/api/guest/totp/replace/confirm', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, code }),
    }),
  totpChallenge: (code: string) =>
    guestRequest<GuestSession>('/api/guest/totp/challenge', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  inviteGate: async (inviteId: string): Promise<{ text: string; gate: GuestInviteGate }> => {
    const path = `/api/guest/invites/${encodeURIComponent(inviteId)}/gate`;
    const response = await fetch(path, { credentials: 'include' });
    if (!response.ok) {
      let code = response.status === 429 || response.status === 503
        ? 'rate_limited'
        : 'invite_not_found';
      try {
        const body = (await response.json()) as { code?: string };
        if (body.code) code = body.code;
      } catch {
        /* ignore */
      }
      throw Object.assign(new Error(code), {
        status: response.status,
        code,
      });
    }
    const text = await response.text();
    return { text, gate: JSON.parse(text) as GuestInviteGate };
  },
  inviteVerify: (inviteId: string, signature: string) =>
    guestRequest<GuestInviteVerify>(`/api/guest/invites/${encodeURIComponent(inviteId)}/verify`, {
      method: 'POST',
      body: JSON.stringify({ signature }),
    }),
  signup: (payload: {
    email: string;
    displayName: string;
    organization?: string | null;
    loginVerifier: string;
    vaultBlob: Uint8Array;
    vaultSalt: Uint8Array;
    vkWrapPassphrase: Uint8Array;
    userPublicKey: Uint8Array;
    vaultVersion: number;
  }) =>
    guestRequest<GuestSession>('/api/guest/signup', {
      method: 'POST',
      body: JSON.stringify({
        email: payload.email,
        displayName: payload.displayName,
        organization: payload.organization,
        loginVerifier: payload.loginVerifier,
        vaultBlob: encodeBase64(payload.vaultBlob),
        vaultSalt: encodeBase64(payload.vaultSalt),
        vkWrapPassphrase: encodeBase64(payload.vkWrapPassphrase),
        userPublicKey: encodeBase64(payload.userPublicKey),
        vaultVersion: payload.vaultVersion,
      }),
    }),
  claimInvite: (inviteId: string, wrappedRoomKey: string, migrateTotp = true) =>
    guestRequest<{
      roomId: string;
      guestRole: string;
      allowCommenting: boolean;
      requireGuestTotp: boolean;
      totpMigrated?: boolean;
    }>(`/api/guest/invites/${encodeURIComponent(inviteId)}/claim`, {
      method: 'POST',
      body: JSON.stringify({ wrappedRoomKey, migrateTotp }),
    }),
  fetchClaimEnvelope: async (inviteId: string): Promise<Uint8Array> => {
    const path = `/api/guest/invites/${encodeURIComponent(inviteId)}/envelope`;
    const response = await fetch(path, {
      credentials: 'include',
      headers: csrfHeadersForRequest(path, 'GET'),
    });
    if (!response.ok) {
      let code = 'request_failed';
      try {
        const body = (await response.json()) as { error?: string };
        if (body.error) code = body.error;
      } catch {
        /* ignore */
      }
      const error = new Error(code) as Error & { status?: number; code?: string };
      error.status = response.status;
      error.code = code;
      throw error;
    }
    return new Uint8Array(await response.arrayBuffer());
  },
  listRooms: () => guestRequest<GuestRoomSummary[]>('/api/guest/rooms'),
  getRoom: (roomId: string) =>
    guestRequest<GuestRoomSummary>(`/api/guest/rooms/${encodeURIComponent(roomId)}`),
  getWrappedRoomKey: (roomId: string) =>
    guestRequest<{ wrappedRoomKey: string }>(`${roomBase(roomId)}/wrapped-key`),
  roomContext: (roomId: string) =>
    guestRequest<SedrGuestRoomContext>(`${roomBase(roomId)}/room`),
  listRequirements: (roomId: string) =>
    guestRequest<SedrRequirement[]>(`${roomBase(roomId)}/requirements`),
  listPhases: (roomId: string) => guestRequest<SedrPhasesList>(`${roomBase(roomId)}/phases`),
  listCategories: (roomId: string) => guestRequest<SedrCategory[]>(`${roomBase(roomId)}/categories`),
  approveRequirement: (roomId: string, requirementId: string) =>
    guestRequest<void>(`${roomBase(roomId)}/requirements/${encodeURIComponent(requirementId)}/approve`, {
      method: 'POST',
    }),
  denyRequirement: (roomId: string, requirementId: string, comment: string) =>
    guestRequest<void>(`${roomBase(roomId)}/requirements/${encodeURIComponent(requirementId)}/deny`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),
  bulkApproveRequirements: (roomId: string, requirementIds: string[]) =>
    guestRequest<void>(`${roomBase(roomId)}/requirements/bulk-approve`, {
      method: 'POST',
      body: JSON.stringify({ requirementIds }),
    }),
  bulkDenyRequirements: (roomId: string, requirementIds: string[], comment: string) =>
    guestRequest<void>(`${roomBase(roomId)}/requirements/bulk-deny`, {
      method: 'POST',
      body: JSON.stringify({ requirementIds, comment }),
    }),
  listFiles: (roomId: string) => guestRequest<SedrFile[]>(`${roomBase(roomId)}/files`),
  addFile: (roomId: string, payload: CreateSedrFilePayload) =>
    guestRequest<SedrFile>(`${roomBase(roomId)}/files`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchFile: (roomId: string, fileId: string, payload: Record<string, unknown>) =>
    guestRequest<SedrFile>(`${roomBase(roomId)}/files/${encodeURIComponent(fileId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  recordFileOpened: (roomId: string, fileId: string) =>
    guestRequest<void>(`${roomBase(roomId)}/files/${encodeURIComponent(fileId)}/opened`, {
      method: 'POST',
    }),
  listAuditEvents: (roomId: string) =>
    guestRequest<SedrAuditEvent[]>(`${roomBase(roomId)}/audit-events`),
  listComments: (roomId: string, requirementId: string) =>
    guestRequest<SedrComment[]>(
      `${roomBase(roomId)}/requirements/${encodeURIComponent(requirementId)}/comments`,
    ),
  searchRequirementsByComment: (roomId: string, q: string) =>
    guestRequest<{ requirementIds: string[] }>(`${roomBase(roomId)}/requirements/comment-search`, {
      method: 'POST',
      body: JSON.stringify({ query: q }),
    }),
  addComment: (roomId: string, requirementId: string, body: string) =>
    guestRequest<SedrComment>(
      `${roomBase(roomId)}/requirements/${encodeURIComponent(requirementId)}/comments`,
      { method: 'POST', body: JSON.stringify({ body }) },
    ),
};
